/**
 * FiKpiRow — 8 top-line tiles for the FI owner cockpit.
 * Ingresos, Costo Total, Utilidad Bruta, Margen%, MRR, ARR, NRR (optional),
 * Pipeline (muted/dashed — it's a projection, not booked revenue).
 */
import type { FiOverviewResponse, DealsSummary } from './fiTypes'
import { formatCurrency, formatPct, marginTone } from './fiFormat'

interface FiKpiRowProps {
  current: FiOverviewResponse['current']
  pipeline: DealsSummary
  /** NRR (%) — comes from the separate SaaS-metrics endpoint; omit to hide the tile. */
  nrr?: number | null
}

function Tile({ label, value, sub, valueClassName, dashed }: {
  label: string
  value: string
  sub?: string
  valueClassName?: string
  dashed?: boolean
}) {
  return (
    <div className={`bg-gray-800 rounded-xl p-4 ${dashed ? 'border border-dashed border-gray-600' : ''}`}>
      <p className="text-gray-400 text-xs uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${valueClassName || 'text-white'}`}>{value}</p>
      {sub && <p className="text-gray-500 text-xs mt-0.5">{sub}</p>}
    </div>
  )
}

export default function FiKpiRow({ current, pipeline, nrr }: FiKpiRowProps) {
  const totalCost = current.cogs + current.overhead
  const tone = marginTone(current.marginPct)

  const cols = nrr != null ? 'lg:grid-cols-8' : 'lg:grid-cols-7'

  return (
    <div className={`grid grid-cols-2 md:grid-cols-4 ${cols} gap-3`}>
      <Tile label="Ingresos" value={formatCurrency(current.revenue)} />
      <Tile label="Costo Total" value={formatCurrency(totalCost)}
        sub={`COGS ${formatCurrency(current.cogs)} + Overhead ${formatCurrency(current.overhead)}`} />
      <Tile label="Utilidad Bruta" value={formatCurrency(current.grossProfit)}
        valueClassName={current.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'} />
      <Tile label="Margen %" value={formatPct(current.marginPct)} valueClassName={tone.text} />
      <Tile label="MRR" value={formatCurrency(current.mrr)} />
      <Tile label="ARR" value={formatCurrency(current.arr)} sub="MRR x 12" />
      {nrr != null && (
        <Tile label="NRR" value={formatPct(nrr)}
          valueClassName={nrr >= 100 ? 'text-green-400' : nrr >= 90 ? 'text-yellow-400' : 'text-red-400'} />
      )}
      <Tile
        label="Pipeline"
        value={formatCurrency(pipeline.weightedPipelineMrr)}
        sub={`${pipeline.openCount} deal(s) abiertos · ponderado`}
        valueClassName="text-gray-300"
        dashed
      />
    </div>
  )
}
